/**
 * Customer-deactivation wizard — staff's permanent "delete" for a customer.
 *
 * Walks an operator through deactivating a customer: a preflight summary of
 * everything the cascade touches (sites, groups, radios, users, alarms, and
 * the billing consequence), a choice of what happens to the customer's radios
 * (relocate to a picked inventory destination, or purge and deactivate their
 * SIMs), and a type-the-name confirmation. The deactivate call enqueues a
 * background cascade and returns immediately, so on confirm the dialog polls
 * the customer's `deactivated` flag and reflects completion before routing
 * back to the customers list. The action is permanent — no reactivation
 * affordance is offered here.
 *
 * Contains:
 * - CustomerDeactivateDialog — the three-step dialog (default export)
 */

import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import PropTypes from 'prop-types';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { deactivateCustomer, fetchCustomer, fetchCustomerDeactivatePreflight } from '@sophocles/api/customer';
import CustomerAutocomplete from '@sophocles/components/CustomerAutocomplete';
import FormErrorAlert from '@sophocles/components/FormErrorAlert';
import ResponsiveDialog from '@sophocles/components/ResponsiveDialog';
import { addToast } from '@sophocles/features/toast';
import { parseApiError } from '@sophocles/utils/apiErrors';

import {
  DESTINATION_MODES,
  RADIO_ACTIONS,
  canProceedFromRadioStep,
  defaultDestinationId,
  isConfirmationMatch,
  isRelocateAvailable,
  nextStep,
  prevStep,
} from './customerDeactivateSteps';

// Pulls a radio_actions consequence count for the given action value —
// the preflight's per-option counts (radios_relocated / sim_deactivations).
function radioActionCount(preflight, value, countKey) {
  return preflight?.radio_actions?.find(entry => entry.value === value)?.[countKey] ?? 0;
}

// The deactivate POST enqueues a background cascade; poll the customer's
// `deactivated` flag until it flips (or give up and let the operator check
// back). 2s cadence, ~60s ceiling.
const POLL_INTERVAL_MS = 2000;
const POLL_MAX_ATTEMPTS = 30;

const CUSTOMERS_ROUTE = '/support/customers';

const sleep = ms => new Promise(resolve => setTimeout(resolve, ms));

// ── Step bodies ──────────────────────────────────────────────────────────────

function SummaryLine({ label, value }) {
  return (
    <Stack direction="row" justifyContent="space-between" spacing={2}>
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body2" fontWeight={600}>
        {value}
      </Typography>
    </Stack>
  );
}

SummaryLine.propTypes = { label: PropTypes.node, value: PropTypes.node };

function SummaryStep({ preflight, t }) {
  return (
    <Stack spacing={2}>
      <Typography variant="body2">
        {t('customerDeactivate.summaryIntro', 'Deactivating this customer will affect:')}
      </Typography>
      <Stack spacing={1}>
        <SummaryLine
          label={t('customerDeactivate.sites', 'Sites deactivated')}
          value={t('customerDeactivate.sitesValue', '{{active}} of {{total}}', {
            active: preflight.sites.active,
            total: preflight.sites.total,
          })}
        />
        <SummaryLine
          label={t('customerDeactivate.groupsDeactivated', 'Site groups deactivated')}
          value={preflight.site_groups.count}
        />
        <SummaryLine label={t('customerDeactivate.radiosOnSite', 'Radios on-site')} value={preflight.radios.on_site} />
        <SummaryLine
          label={t('customerDeactivate.radiosInInventory', 'Radios in inventory')}
          value={preflight.radios.in_inventory}
        />
        <SummaryLine label={t('customerDeactivate.users', 'Users')} value={preflight.users.count} />
        <SummaryLine label={t('customerDeactivate.activeAlarms', 'Active alarms')} value={preflight.alarms.active} />
        <SummaryLine
          label={t('customerDeactivate.billingStop', 'Radios that stop billing')}
          value={preflight.billing.radios_stop_billing}
        />
        <SummaryLine
          label={t('customerDeactivate.simsOnPurge', 'SIMs deactivated if purged')}
          value={preflight.billing.sim_deactivations_on_purge}
        />
      </Stack>
    </Stack>
  );
}

SummaryStep.propTypes = { preflight: PropTypes.object.isRequired, t: PropTypes.func.isRequired };

// The relocate destination picker: a global inventory location (a Select over
// radio_destinations, warehouse pre-selected) or — when the backend supports
// it — another active customer via the shared customer autocomplete.
function RelocateDestination({
  destinations,
  supportsCustomerDestination,
  destinationMode,
  onDestinationModeChange,
  destinationId,
  onDestinationChange,
  destinationCustomerId,
  onDestinationCustomerChange,
  t,
}) {
  const locationPicker = (
    <FormControl size="small" fullWidth sx={{ maxWidth: 320 }}>
      <InputLabel id="customer-deactivate-destination-label">
        {t('customerDeactivate.destinationLabel', 'Move radios to')}
      </InputLabel>
      <Select
        labelId="customer-deactivate-destination-label"
        label={t('customerDeactivate.destinationLabel', 'Move radios to')}
        value={destinationId ?? ''}
        onChange={event => onDestinationChange(event.target.value)}
      >
        {destinations.map(destination => (
          <MenuItem key={destination.id} value={destination.id}>
            {destination.label}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  if (!supportsCustomerDestination) {
    return <Box sx={{ mt: 1, mb: 1, ml: 4 }}>{locationPicker}</Box>;
  }

  return (
    <Box sx={{ mt: 1, mb: 1, ml: 4 }}>
      <RadioGroup value={destinationMode} onChange={event => onDestinationModeChange(event.target.value)}>
        <FormControlLabel
          value={DESTINATION_MODES.LOCATION}
          control={<Radio size="small" />}
          label={
            <Typography variant="body2">
              {t('customerDeactivate.destinationModeLocation', 'A global inventory location')}
            </Typography>
          }
        />
        {destinationMode === DESTINATION_MODES.LOCATION ? <Box sx={{ ml: 4, mt: 0.5 }}>{locationPicker}</Box> : null}
        <FormControlLabel
          value={DESTINATION_MODES.CUSTOMER}
          control={<Radio size="small" />}
          label={
            <Typography variant="body2">
              {t('customerDeactivate.destinationModeCustomer', 'Another customer')}
            </Typography>
          }
        />
        {destinationMode === DESTINATION_MODES.CUSTOMER ? (
          <Box sx={{ ml: 4, mt: 0.5, maxWidth: 320 }}>
            <CustomerAutocomplete
              fullWidth
              name="deactivate-destination-customer"
              value={destinationCustomerId ?? ''}
              label={t('customerDeactivate.destinationCustomerLabel', 'Destination customer')}
              onChange={(event, selectedCustomerId) => onDestinationCustomerChange(selectedCustomerId ?? null)}
            />
          </Box>
        ) : null}
      </RadioGroup>
    </Box>
  );
}

RelocateDestination.propTypes = {
  destinations: PropTypes.array.isRequired,
  supportsCustomerDestination: PropTypes.bool.isRequired,
  destinationMode: PropTypes.string.isRequired,
  onDestinationModeChange: PropTypes.func.isRequired,
  destinationId: PropTypes.number,
  onDestinationChange: PropTypes.func.isRequired,
  destinationCustomerId: PropTypes.number,
  onDestinationCustomerChange: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
};

function RadioStep({
  preflight,
  radioAction,
  onRadioActionChange,
  destinationMode,
  onDestinationModeChange,
  destinationId,
  onDestinationChange,
  destinationCustomerId,
  onDestinationCustomerChange,
  t,
}) {
  const destinations = preflight.radio_destinations ?? [];
  const relocateAvailable = isRelocateAvailable(destinations);
  const supportsCustomerDestination = preflight.supports_customer_destination === true;
  const relocatedCount = radioActionCount(preflight, RADIO_ACTIONS.INVENTORY, 'radios_relocated');
  const simDeactivationCount = radioActionCount(preflight, RADIO_ACTIONS.PURGE, 'sim_deactivations');

  return (
    <Stack spacing={1}>
      <Typography variant="body2">
        {t('customerDeactivate.radioChoiceTitle', "What should happen to this customer's radios?")}
      </Typography>
      <RadioGroup value={radioAction} onChange={event => onRadioActionChange(event.target.value)}>
        <FormControlLabel
          value={RADIO_ACTIONS.INVENTORY}
          control={<Radio />}
          disabled={!relocateAvailable}
          label={
            <Box>
              <Typography variant="body2">{t('customerDeactivate.inventoryLabel', 'Relocate radios')}</Typography>
              <Typography variant="caption" color="text.secondary">
                {t('customerDeactivate.inventoryConsequence', '{{count}} radios relocated', {
                  count: relocatedCount,
                })}
              </Typography>
            </Box>
          }
        />
        {!relocateAvailable ? (
          <Alert severity="warning" sx={{ ml: 4, mb: 1 }}>
            {t(
              'customerDeactivate.relocateUnavailable',
              'Relocating radios is unavailable — no destination was offered for this customer. Purge the radios instead.',
            )}
          </Alert>
        ) : null}
        {radioAction === RADIO_ACTIONS.INVENTORY && relocateAvailable ? (
          <RelocateDestination
            destinations={destinations}
            supportsCustomerDestination={supportsCustomerDestination}
            destinationMode={destinationMode}
            onDestinationModeChange={onDestinationModeChange}
            destinationId={destinationId}
            onDestinationChange={onDestinationChange}
            destinationCustomerId={destinationCustomerId}
            onDestinationCustomerChange={onDestinationCustomerChange}
            t={t}
          />
        ) : null}
        <FormControlLabel
          value={RADIO_ACTIONS.PURGE}
          control={<Radio />}
          label={
            <Box>
              <Typography variant="body2">
                {t('customerDeactivate.purgeLabel', 'Purge radios (deactivate SIMs)')}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {t('customerDeactivate.purgeConsequence', '{{count}} SIMs deactivated', {
                  count: simDeactivationCount,
                })}
              </Typography>
            </Box>
          }
        />
      </RadioGroup>
    </Stack>
  );
}

RadioStep.propTypes = {
  preflight: PropTypes.object.isRequired,
  radioAction: PropTypes.string.isRequired,
  onRadioActionChange: PropTypes.func.isRequired,
  destinationMode: PropTypes.string.isRequired,
  onDestinationModeChange: PropTypes.func.isRequired,
  destinationId: PropTypes.number,
  onDestinationChange: PropTypes.func.isRequired,
  destinationCustomerId: PropTypes.number,
  onDestinationCustomerChange: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
};

function ConfirmStep({ customerName, typed, onTyped, errorMessages, t }) {
  return (
    <Stack spacing={2}>
      <Alert severity="error">
        {t(
          'customerDeactivate.permanenceWarning',
          'This permanently deactivates the customer. Radios that are moved or purged are not restored.',
        )}
      </Alert>
      <Typography variant="body2">
        {t('customerDeactivate.typePrompt', 'Type {{name}} to confirm', { name: customerName })}
      </Typography>
      <TextField
        value={typed}
        onChange={event => onTyped(event.target.value)}
        label={t('customerDeactivate.typeLabel', 'Customer name')}
        fullWidth
        autoComplete="off"
        autoFocus
      />
      <FormErrorAlert messages={errorMessages} />
    </Stack>
  );
}

ConfirmStep.propTypes = {
  customerName: PropTypes.string,
  typed: PropTypes.string.isRequired,
  onTyped: PropTypes.func.isRequired,
  errorMessages: PropTypes.arrayOf(PropTypes.string),
  t: PropTypes.func.isRequired,
};

// ── Dialog ───────────────────────────────────────────────────────────────────

export default function CustomerDeactivateDialog({ open, onClose, customerId, customerName }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [step, setStep] = React.useState('summary');
  const [preflight, setPreflight] = React.useState(null);
  const [loadStatus, setLoadStatus] = React.useState('loading');
  const [radioAction, setRadioAction] = React.useState(RADIO_ACTIONS.INVENTORY);
  const [destinationMode, setDestinationMode] = React.useState(DESTINATION_MODES.LOCATION);
  const [destinationId, setDestinationId] = React.useState(null);
  const [destinationCustomerId, setDestinationCustomerId] = React.useState(null);
  const [typed, setTyped] = React.useState('');
  const [processing, setProcessing] = React.useState(false);
  const [errorMessages, setErrorMessages] = React.useState([]);

  const activeRef = React.useRef(false);

  // Load the preflight summary each time the dialog opens; reset all
  // wizard state so a re-open never shows a stale prior run.
  React.useEffect(() => {
    if (!open) return undefined;
    activeRef.current = true;
    setStep('summary');
    setPreflight(null);
    setLoadStatus('loading');
    setRadioAction(RADIO_ACTIONS.INVENTORY);
    setDestinationMode(DESTINATION_MODES.LOCATION);
    setDestinationId(null);
    setDestinationCustomerId(null);
    setTyped('');
    setProcessing(false);
    setErrorMessages([]);
    fetchCustomerDeactivatePreflight(customerId)
      .then(data => {
        if (!activeRef.current) return;
        setPreflight(data);
        setDestinationId(defaultDestinationId(data?.radio_destinations));
        // Older backends omit radio_destinations entirely; relocate can't work,
        // so default the disposition to purge rather than a dead-end picker.
        setRadioAction(isRelocateAvailable(data?.radio_destinations) ? RADIO_ACTIONS.INVENTORY : RADIO_ACTIONS.PURGE);
        setLoadStatus('ready');
      })
      .catch(() => {
        if (!activeRef.current) return;
        setLoadStatus('error');
      });
    return () => {
      activeRef.current = false;
    };
  }, [open, customerId]);

  const confirmName = preflight?.customer?.name ?? customerName ?? '';

  const finish = message => {
    if (!activeRef.current) return;
    dispatch(addToast({ message, level: 'success' }));
    onClose();
    navigate(CUSTOMERS_ROUTE);
  };

  const pollUntilDeactivated = async () => {
    for (let attempt = 0; attempt < POLL_MAX_ATTEMPTS; attempt += 1) {
      await sleep(POLL_INTERVAL_MS);
      if (!activeRef.current) return;
      const customer = await fetchCustomer(customerId).catch(() => null);
      if (!activeRef.current) return;
      if (customer?.deactivated) {
        finish(t('customerDeactivate.success', '{{name}} has been deactivated.', { name: confirmName }));
        return;
      }
    }
    // Cascade is still running past the poll ceiling — let the operator move on.
    if (!activeRef.current) return;
    dispatch(
      addToast({
        message: t(
          'customerDeactivate.timeout',
          'Deactivation is still processing. It may take a few more minutes to complete.',
        ),
        level: 'info',
      }),
    );
    onClose();
    navigate(CUSTOMERS_ROUTE);
  };

  // The mutually-exclusive relocate target for the deactivate POST. Purge and
  // the customer-mode path each omit the other's field; the fetcher applies the
  // BE precedence (destination_customer_id wins) from whatever is present.
  const relocateDestination = () => {
    if (radioAction !== RADIO_ACTIONS.INVENTORY) {
      return {};
    }
    if (destinationMode === DESTINATION_MODES.CUSTOMER) {
      return { destinationCustomerId };
    }
    return { inventoryLocationId: destinationId };
  };

  const submit = async () => {
    setProcessing(true);
    setErrorMessages([]);
    try {
      await deactivateCustomer(customerId, radioAction, relocateDestination());
      await pollUntilDeactivated();
    } catch (error) {
      if (!activeRef.current) return;
      setProcessing(false);
      setErrorMessages(parseApiError(error).nonFieldMessages);
    }
  };

  const handlePrimary = () => {
    if (step === 'confirm') {
      submit();
    } else {
      setStep(current => nextStep(current));
    }
  };

  const canSubmit = (() => {
    if (step === 'summary') return loadStatus === 'ready';
    if (step === 'radio') {
      return canProceedFromRadioStep({
        radioAction,
        destinationMode,
        destinationId,
        destinationCustomerId,
        destinations: preflight?.radio_destinations,
        deactivatingCustomerId: customerId,
      });
    }
    return isConfirmationMatch(typed, confirmName);
  })();

  const submitText =
    step === 'confirm' ? t('customerDeactivate.confirm', 'Deactivate customer') : t('actions.next', 'Next');

  const backAction =
    step === 'summary' || processing ? null : (
      <Button variant="text" onClick={() => setStep(current => prevStep(current))}>
        {t('actions.back', 'Back')}
      </Button>
    );

  return (
    <ResponsiveDialog
      open={open}
      onClose={processing ? undefined : onClose}
      title={t('customerDeactivate.dialogTitle', 'Deactivate {{name}}', { name: confirmName })}
      maxWidth="sm"
      submitText={submitText}
      submitColor={step === 'confirm' ? 'error' : 'primary'}
      canSubmit={canSubmit}
      hideCancel={processing}
      extraActions={backAction}
      onSubmit={processing ? undefined : handlePrimary}
    >
      {processing ? (
        <Stack spacing={2} alignItems="center" sx={{ py: 2 }}>
          <CircularProgress />
          <Typography variant="body2" color="text.secondary">
            {t('customerDeactivate.processingMessage', 'This may take a moment.')}
          </Typography>
        </Stack>
      ) : loadStatus === 'loading' ? (
        <Stack spacing={2} alignItems="center" sx={{ py: 2 }}>
          <CircularProgress />
        </Stack>
      ) : loadStatus === 'error' ? (
        <Alert severity="error">
          {t('customerDeactivate.loadError', "Couldn't load the deactivation summary. Please try again.")}
        </Alert>
      ) : step === 'summary' ? (
        <SummaryStep preflight={preflight} t={t} />
      ) : step === 'radio' ? (
        <RadioStep
          preflight={preflight}
          radioAction={radioAction}
          onRadioActionChange={setRadioAction}
          destinationMode={destinationMode}
          onDestinationModeChange={setDestinationMode}
          destinationId={destinationId}
          onDestinationChange={setDestinationId}
          destinationCustomerId={destinationCustomerId}
          onDestinationCustomerChange={setDestinationCustomerId}
          t={t}
        />
      ) : (
        <ConfirmStep customerName={confirmName} typed={typed} onTyped={setTyped} errorMessages={errorMessages} t={t} />
      )}
    </ResponsiveDialog>
  );
}

CustomerDeactivateDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  customerId: PropTypes.number.isRequired,
  customerName: PropTypes.string,
};
